import { Box, Button, Group, Stack, Text } from '@mantine/core'

import { ScreenErrorBoundary } from '@/components/feedback/ScreenErrorBoundary'
import { JanelaFlutuante } from '@/components/ui/JanelaFlutuante'
import { TerminalDoServidor } from '@/pages/ssm/TerminalDoServidor'
import {
  despinar,
  digitar,
  encerrarSessao,
  moverJanela,
  ouvir,
  reabrirSessao,
  redimensionar,
  useSsmStore,
  type SessaoNaTela,
} from '@/store/ssm'

/**
 * As janelas de terminal pinadas, desenhadas fora da tela de Servidores.
 *
 * Montado no `AppLayout`, irmão do `<Outlet/>`: é isso que faz a janela sobreviver
 * a trocar de rota. A sessão em si já vive no store de módulo (TASK-10345), então
 * aqui só se desenha — nada de estado próprio.
 *
 * ## Duas decisões que a interface carrega
 *
 * **O ✕ despina; encerrar é botão à parte.** Se o mesmo controle fizesse as duas
 * coisas, alguém perderia o shell achando que fechou uma janela. Só "Encerrar"
 * fecha o socket, e ele diz isso no rótulo.
 *
 * **A janela não some quando a sessão morre.** Fora de `/ssm` não existe o `Alert`
 * da tela de Servidores, então o terminal é o único canal onde o motivo do
 * encerramento está escrito — sumir levaria embora justamente a explicação.
 */

/** Onde a primeira janela nasce, e o quanto cada nova se desloca. */
const PRIMEIRA = { x: 120, y: 120, w: 620, h: 380 }
const DESLOCAMENTO = 28

export function TerminaisPinados() {
  const sessoes = useSsmStore(e => e.sessoes)
  const pinadas = Object.values(sessoes).filter(
    (s: SessaoNaTela) => s.modo === 'flutuante',
  )

  if (pinadas.length === 0) return null

  return (
    <>
      {pinadas.map((s, i) => (
        <JanelaFlutuante
          key={s.instanceId}
          titulo={
            <Group gap={6} wrap="nowrap">
              <Text size="xs" fw={600} c="#c9d1d9" truncate>
                {s.nome}
              </Text>
              <Text size="xs" c="dimmed" truncate>
                · {s.tenantNome}
              </Text>
              {s.estado !== 'aberta' && (
                <Text size="xs" c={s.estado === 'recusada' ? 'red.4' : 'gray.5'}>
                  ({s.estado})
                </Text>
              )}
            </Group>
          }
          acoes={
            <Button
              size="compact-xs"
              variant="subtle"
              color="red"
              onClick={() => encerrarSessao(s.instanceId)}
            >
              Encerrar
            </Button>
          }
          inicial={
            s.geometria ?? {
              ...PRIMEIRA,
              x: PRIMEIRA.x + i * DESLOCAMENTO,
              y: PRIMEIRA.y + i * DESLOCAMENTO,
            }
          }
          onFechar={() => despinar(s.instanceId)}
          aoMover={g => moverJanela(s.instanceId, g)}
        >
          {/* Boundary POR JANELA, e por dentro dela: o `<Outlet/>` tem o dele,
              mas o `<TerminaisPinados/>` e IRMAO do Outlet, entao sem este um
              erro de render num terminal sobe ate a raiz e derruba o console
              com as seis janelas. Por DENTRO para a barra de titulo sobreviver
              ao erro — senao a pessoa fica com um retangulo vermelho preso na
              tela, sem botao de fechar. */}
          <ScreenErrorBoundary fallback="minimal" screenName={`terminal ${s.nome}`}>
            <Stack gap={0} h="100%">
              {s.estado !== 'aberta' && s.estado !== 'conectando' && (
                <Group
                  gap={8}
                  px={10}
                  py={6}
                  style={{ borderBottom: '1px solid var(--ai-console-border)' }}
                >
                  <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                    Sessão {s.estado}. Abrir de novo inicia outra sessão neste
                    servidor, com nova gravação — não é a mesma continuando.
                  </Text>
                  <Button
                    size="compact-xs"
                    variant="light"
                    onClick={() => reabrirSessao(s.instanceId)}
                  >
                    Abrir de novo
                  </Button>
                </Group>
              )}
              <Box style={{ flex: 1, minHeight: 0 }}>
                <TerminalDoServidor
                  instanceId={s.instanceId}
                  historico={useSsmStore.getState().historico}
                  ouvir={ouvir}
                  digitar={digitar}
                  redimensionar={redimensionar}
                />
              </Box>
            </Stack>
          </ScreenErrorBoundary>
        </JanelaFlutuante>
      ))}
    </>
  )
}
